import {
  DerivedDataAggregationFunction,
  DerivedDataRollingType,
  PermissionBit,
  PPGDerivedDataGroup,
  PPGDerivedDataSchemaWithPPGResponse,
  QuickLookTasks,
} from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";
import { PPGQuickLook } from "../../../models/quick-look-analyses";
import { DERIVED_MEAN_TYPE } from "./derived-data-names";

export type DerivedDataItem = PPGDerivedDataSchemaWithPPGResponse & {
  ppgConditions?: string[];
  ppgDevice?: string[];
  ppgQuickLook?: PPGQuickLook | null;
};

export class DerivedData extends DataModel<DerivedDataItem> {
  private quickLookByUuid = new Map<string, PPGQuickLook>();

  // constants

  dataGroups = {
    [PPGDerivedDataGroup.hr]: "HR",
    [PPGDerivedDataGroup.hrv]: "HR Variability",
    [PPGDerivedDataGroup.spo2]: "SpO2",
  };
  aggTypes = {
    [DerivedDataRollingType.full]: "full",
    [DerivedDataRollingType.window]: "windowed",
  };
  aggFuncs = {
    [DerivedDataAggregationFunction.mean]: "mean",
    [DerivedDataAggregationFunction.median]: "median",
    [DerivedDataAggregationFunction.std]: "STD",
    [DerivedDataAggregationFunction.min]: "min",
    [DerivedDataAggregationFunction.max]: "max",
    [DerivedDataAggregationFunction.sdnn]: "SDNN",
    [DerivedDataAggregationFunction.sdsd]: "SDSD",
    [DerivedDataAggregationFunction.rmssd]: "RMSSD",
    [DerivedDataAggregationFunction.pnn50]: "pNN50",
    [DerivedDataAggregationFunction.pnn20]: "pNN20",
    [DerivedDataAggregationFunction.psd_lf]: "low frequency power",
    [DerivedDataAggregationFunction.psd_hf]: "high frequency power",
    [DerivedDataAggregationFunction.sample_entropy]: "entropy",
  };
  spo2Name = DERIVED_MEAN_TYPE[PPGDerivedDataGroup.spo2];
  hrName = DERIVED_MEAN_TYPE[PPGDerivedDataGroup.hr];

  constructor(
    data: DerivedDataItem[],
    permissions: PermissionBit[],
    userUUID: string,
    private api?: ApiConnector,
    private qlAnalysisType?: QuickLookTasks,
  ) {
    super(data, permissions, userUUID);
    this.defaultFilter = {
      rolling: [DerivedDataRollingType.full],
      aggregation_function: [
        DerivedDataAggregationFunction.mean,
        DerivedDataAggregationFunction.sdnn,
        DerivedDataAggregationFunction.sdsd,
        DerivedDataAggregationFunction.pnn50,
        DerivedDataAggregationFunction.psd_lf,
        DerivedDataAggregationFunction.psd_hf,
        DerivedDataAggregationFunction.sample_entropy,
      ],
    };

    // access
    this.access = {
      editRules: { medical: AccessRules.none, monitoring: AccessRules.visible },
      deleteRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      historyRequiresMonitoring: false,
    };
  }

  init() {
    const activeQuickLookUuids = new Set<string>();

    this.data = this.data.map((item: PPGDerivedDataSchemaWithPPGResponse) => {
      let ppgQuickLook: PPGQuickLook | null = null;
      if (this.api && this.qlAnalysisType) {
        const rowUuid = String(item.uuid);
        const quickLookUuids = [item["ppg"]["uuid"]];
        ppgQuickLook =
          this.quickLookByUuid.get(rowUuid) ??
          new PPGQuickLook(this.api, this.qlAnalysisType, quickLookUuids);
        ppgQuickLook.syncSelection(this.qlAnalysisType, quickLookUuids);
        this.quickLookByUuid.set(rowUuid, ppgQuickLook);
        activeQuickLookUuids.add(rowUuid);
      }
      const ppgCond = item.ppg.measurement_condition;
      const ppgDevice = item.ppg.measurement_device;
      return {
        ...item,
        ["ppgConditions"]: [ppgCond],
        ["ppgDevice"]: [ppgDevice],
        ["ppgQuickLook"]: ppgQuickLook,
      };
    });

    for (const uuid of this.quickLookByUuid.keys()) {
      if (!activeQuickLookUuids.has(uuid)) {
        this.quickLookByUuid.delete(uuid);
      }
    }

    super.init();
  }

  filterSpO2() {
    this.data = this.data.filter((item) => {
      return (
        item.measurement_type === this.spo2Name &&
        item.data_group === PPGDerivedDataGroup.spo2
      );
    });
  }

  filterHR() {
    this.data = this.data.filter((item) => {
      return (
        item.measurement_type === this.hrName &&
        item.data_group === PPGDerivedDataGroup.hr
      );
    });
  }
}
